import { RouterLink, useRoute } from 'vue-router';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import {
  createEducationHttpRequest,
  createEducationModule,
  createEducationProductionApi,
} from '@qbeek/education-module';
import { collectModuleMenuItems, collectModuleRoutes, collectModuleStoreModules } from './registry/moduleRegistry';
import i18n from '@/i18n';

const toast = useToast();

const SMOOZIE_PUBLIC_BASE_URL = process.env.VUE_APP_SMOOZIE_PUBLIC_BASE_URL || 'https://smoozi.io';
const EDUCATION_ERROR_TOAST_TIMEOUT_MS = 5000;
const EDUCATION_AUTH_TOKEN_ERROR_MESSAGES = new Set(['invalid token']);
const EDUCATION_ERROR_TOAST_ALLOWLIST = new Set([
  'education.lessonPublishRequiresMaterialError',
  'education.coursePublishRequiresMaterialError',
  'education.lessonSectionMustBeActiveError',
  'education.lessonMustBeActiveToAttachError',
  'education.lessonMaterialInvalidFileFormatError',
  'education.lessonFileUploadSizeLimitError',
  'education.lessonMaterialUploadFailedError',
  'education.lessonMaterialSortOrderConflictError',
  'education.quizGamePublishRequiresCaseError',
  'education.lessonBuilderRequiredFieldsError',
  'education.quizBuilderRequiredFieldsError',
  'education.gameBuilderRequiredFieldsError',
  'education.lessonBuilderSaveFailedError',
  'education.quizBuilderSaveFailedError',
  'education.gameBuilderSaveFailedError',
]);
const EDUCATION_ERROR_FALLBACK_MESSAGES = {
  'education.lessonFileUploadSizeLimitError': {
    en: 'File must be 10 MB or smaller',
    ru: 'Файл должен быть не больше 10 МБ',
    uk: 'Файл має бути не більше 10 МБ',
  },
};

function getStoredAuthValue(key) {
  return localStorage.getItem(key) || sessionStorage.getItem(key) || null;
}

function getCurrentLocale() {
  const locale = i18n.global.locale;

  return locale?.value || locale;
}

function createEducationAuthContext() {
  return {
    accessToken: getStoredAuthValue('accessToken'),
    locale: getCurrentLocale(),
    merchantId: getStoredAuthValue('merchantId'),
    token: getStoredAuthValue('accessToken'),
    userId: getStoredAuthValue('uid') || getStoredAuthValue('userid'),
  };
}

function resolveSmoozieFilePath(uploadResult = {}) {
  return uploadResult.file?.link?.path ||
    uploadResult.data?.file?.link?.path ||
    uploadResult.link?.path ||
    uploadResult.path ||
    '';
}

function getEducationErrorStatus(error = {}) {
  return Number(error.status || error.response?.status || error.data?.status || error.response?.data?.status || 0);
}

function getEducationErrorMessage(error) {
  if (typeof error === 'string') return error;

  return error?.message ||
    error?.response?.data?.message ||
    error?.data?.message ||
    '';
}

function isEducationAuthTokenError(error) {
  const status = getEducationErrorStatus(error);
  const message = getEducationErrorMessage(error).trim().toLowerCase();

  return status === 401 || EDUCATION_AUTH_TOKEN_ERROR_MESSAGES.has(message);
}

function resolveEducationErrorMessage(error) {
  if (error?.educationErrorKey) {
    const localizedMessage = i18n.global.t(error.educationErrorKey);

    if (localizedMessage !== error.educationErrorKey) return localizedMessage;

    const locale = String(getCurrentLocale()).split('-')[0];
    const fallbackMessage = EDUCATION_ERROR_FALLBACK_MESSAGES[error.educationErrorKey]?.[locale];

    if (fallbackMessage) return fallbackMessage;
  }

  return getEducationErrorMessage(error) ||
    i18n.global.t('education.errorToast');
}

function isEducationToastAllowed(error) {
  return EDUCATION_ERROR_TOAST_ALLOWLIST.has(error?.educationErrorKey || getEducationErrorMessage(error));
}

function buildSmooziePublicUrl(path = '') {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;

  return `${SMOOZIE_PUBLIC_BASE_URL}/${String(path).replace(/^\/+/, '')}`;
}

async function uploadEducationImage({ file, fileName = file?.name || 'education-cover.webp' } = {}) {
  if (!file) return '';

  const uploadResult = await realEducationApi.uploadSmoozieFile(createEducationAuthContext(), {
    file,
    fileName,
  });

  return buildSmooziePublicUrl(resolveSmoozieFilePath(uploadResult));
}

const authApi = axios.create({
  baseURL: process.env.VUE_APP_SERVER_AUTH_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

async function refreshEducationToken() {
  const refreshToken = getStoredAuthValue('refreshToken');
  const merchantId = getStoredAuthValue('merchantId');
  const isRememberMe = getStoredAuthValue('isRememberMeChecked') === 'true';

  if (!refreshToken || !merchantId) {
    throw new Error('Cannot refresh token: missing refreshToken or merchantId');
  }

  try {
    const response = await authApi.post('/api/token/refresh', {
      refreshToken,
      merchantId,
    });

    const storage = isRememberMe ? localStorage : sessionStorage;
    storage.setItem('accessToken', response.data.accessToken);
    if (response.data.refreshToken) {
      storage.setItem('refreshToken', response.data.refreshToken);
    }
    if (response.data.expiresAt) {
      storage.setItem('tokenExpiresAt', response.data.expiresAt);
    }

    return response.data.accessToken;
  } catch (error) {
    console.error('Education token refresh failed:', error);
    throw error;
  }
}

const educationHttpRequest = createEducationHttpRequest({
  authTokenProvider: () => Promise.resolve(getStoredAuthValue('accessToken')),
  tokenRefreshProvider: refreshEducationToken,
  baseUrl: process.env.VUE_APP_SERVER_URL || 'http://localhost:3000',
  fetchImplementation: typeof fetch !== 'undefined' ? fetch : undefined,
});

const realEducationApi = createEducationProductionApi({
  basePath: '/operator/v1/smoozie',
  request: educationHttpRequest,
});

export function createAppModuleHost() {
  return {
    RouterLink,
    educationApi: realEducationApi,
    getAuthContext: createEducationAuthContext,
    onError: (error, context) => {
      console.log('[Education module error]', context, error);
      if (isEducationAuthTokenError(error)) return;
      if (!isEducationToastAllowed(error)) return;

      toast.error(resolveEducationErrorMessage(error), {
        timeout: EDUCATION_ERROR_TOAST_TIMEOUT_MS,
      });
    },
    onSuccess: (message) => {
      toast.success(message);
    },
    registerI18nMessages: (messagesByLocale = {}) => {
      Object.entries(messagesByLocale).forEach(([locale, messages]) => {
        i18n.global.mergeLocaleMessage(locale, messages);
      });
    },
    t: (...args) => i18n.global.t(...args),
    uploadImage: uploadEducationImage,
    useRoute,
  };
}

export function createAppModuleRegistry({ host = createAppModuleHost() } = {}) {
  const modules = [
    createEducationModule({ host }),
  ];

  return {
    modules,
    routes: collectModuleRoutes(modules),
    storeModules: collectModuleStoreModules(modules),
    getMenuItems: (config) => collectModuleMenuItems(modules, config),
  };
}

let appModuleRegistry;

export function getAppModuleRegistry() {
  if (!appModuleRegistry) {
    appModuleRegistry = createAppModuleRegistry();
  }

  return appModuleRegistry;
}

export function resetAppModuleRegistryForTests() {
  appModuleRegistry = undefined;
}
